import { FC, ReactNode, useState } from "react";
import { Typography, AutoCloser } from "@earned/core";
import {
  FloatingPortal,
  useFloating,
  flip,
  offset,
  shift,
  Placement,
  useInteractions,
  useHover,
  safePolygon,
  autoUpdate,
} from "@floating-ui/react";
import { Transition } from "@headlessui/react";

interface HoverTooltip {
  children: ReactNode;
  placement?: Placement;
  title?: string;
  description?: string;
  autoClose?: boolean;
  closeAfter?: number;
}

const HoverTooltip: FC<HoverTooltip> = ({
  children,
  description,
  title,
  placement = "bottom",
  autoClose = false,
  closeAfter = 1000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { floatingStyles, refs, context } = useFloating({
    placement,
    strategy: "fixed",
    middleware: [
      flip({ fallbackAxisSideDirection: "start", crossAxis: false }), // prevent flipping on cross axis which can cause jitter
      offset(10),
      shift({
        padding: 8,
        limiter: {
          fn: ({ x, y }) => ({ x, y }), // smooth limiting function
        },
      }),
    ],
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });

  const onHover = useHover(context, {
    move: false,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: false, // allow pointer events to prevent dead zones
      buffer: -5, // negative buffer creates overlap tolerance
    }),
    delay: { open: 75, close: 100 },
    restMs: 500, // time to wait before closing after mouse rest
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([onHover]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        style={{ display: "inline-block" }}
      >
        {children}
      </div>
      <AutoCloser
        autoClose={autoClose}
        closeAfter={closeAfter}
        close={() => setIsOpen(false)}
        open={isOpen}
      />
      <FloatingPortal>
        <div style={{ position: "relative", zIndex: 9999 }}>
          <Transition
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            show={isOpen}
          >
            <div
              className="z-[11] flex max-w-[300px] flex-col space-y-5 rounded-lg border border-green-3 bg-white p-6 font-medium leading-normal text-green-1 shadow-sm"
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                pointerEvents: "auto",
              }}
              {...getFloatingProps()}
            >
              <div className="flex flex-row items-start space-x-2">
                {/* No whitespace-nowrap: it contradicted the panel's
                    max-w-[300px] above, so any content longer than one line's
                    worth ran off the side of the screen instead of wrapping
                    inside the panel. Short labels are unaffected — they still
                    fit on one line. The panel's leading-4 went with it: a 16px
                    line box on 16px text is invisible while nothing wraps, and
                    collides descenders into ascenders as soon as something
                    does. */}
                <div className="flex flex-col space-y-5">
                  {title && (
                    <Typography variant="body" weight="medium" color="primary">
                      {title}
                    </Typography>
                  )}
                  {description && (
                    <Typography variant="body" color="primary">
                      {description}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </FloatingPortal>
    </>
  );
};

export default HoverTooltip;
